import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { CORE_PAGES } from '@/content/pages.js';
import { DEFAULT_OG_ALT, DEFAULT_OG_IMAGE, absoluteUrl } from '@lib/site';

function corePage(routeKey) {
  const entry = CORE_PAGES[routeKey] || CORE_PAGES.home;
  return {
    path: entry.path,
    title: entry.title,
    description: entry.description,
    ogImage: entry.ogImage || DEFAULT_OG_IMAGE
  };
}

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

/**
 * Updates document head on client-side navigations.
 * The production HTML for each route is also written at build time.
 */
export function Seo({ routeKey, title, description, path, ogImage, ogImageAlt, jsonLd, noindex }) {
  const page = corePage(routeKey);
  const resolvedTitle = title || page.title;
  const resolvedDescription = description || page.description;
  const resolvedPath = path || page.path;
  const canonical = absoluteUrl(resolvedPath);
  const image = ogImage || page.ogImage || DEFAULT_OG_IMAGE;
  const imageAlt = ogImageAlt || DEFAULT_OG_ALT;
  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    document.title = resolvedTitle;

    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: resolvedDescription
    });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: noindex ? 'noindex, follow' : 'index, follow'
    });
    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: resolvedTitle
    });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: resolvedDescription
    });
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: canonical
    });
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: image
    });
    upsertMeta('meta[property="og:image:alt"]', {
      property: 'og:image:alt',
      content: imageAlt
    });
    upsertMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: 'website'
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image'
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: resolvedTitle
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: resolvedDescription
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: image
    });
    upsertLink('canonical', canonical);

    const existing = document.getElementById('seo-jsonld');
    if (jsonLdText) {
      const script = existing || document.createElement('script');
      script.id = 'seo-jsonld';
      script.type = 'application/ld+json';
      script.textContent = jsonLdText;
      if (!existing) document.head.appendChild(script);
    } else if (existing) {
      existing.remove();
    }
  }, [resolvedTitle, resolvedDescription, canonical, image, imageAlt, noindex, jsonLdText]);

  return null;
}

Seo.propTypes = {
  routeKey: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  path: PropTypes.string,
  ogImage: PropTypes.string,
  ogImageAlt: PropTypes.string,
  jsonLd: PropTypes.object,
  noindex: PropTypes.bool
};
